// src/admin/pages/OrdersListPage.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FulfillmentStatus,
  OrderListItem,
  OrderListResponse,
  OrderStatus,
  PaymentStatus,
} from "../../types/order";
import { OrdersAPI } from "../../api/orders";
import { useViewportMatch } from "../../hooks/useViewportMatch";

type ListQuery = {
  page?: number;
  limit?: number;
  sort?: string;
  q?: string;
  status?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
};

const ORDER_STATUS_OPTIONS: string[] = [
  "",
  OrderStatus.Pending,
  OrderStatus.Paid,
  OrderStatus.Waiting,
  OrderStatus.CurrentlyWorking,
  OrderStatus.Finishing,
  OrderStatus.WaitingForShipping,
  OrderStatus.Shipped,
  OrderStatus.Delivered,
  OrderStatus.WaitingForPickup,
  OrderStatus.ReadyForPickup,
  OrderStatus.PickedUp,
  OrderStatus.PartiallyRefunded,
  OrderStatus.Refunded,
  OrderStatus.Cancelled,
];
const PAYMENT_STATUS_OPTIONS: string[] = [
  "",
  PaymentStatus.Unpaid,
  PaymentStatus.Pending,
  PaymentStatus.Paid,
  PaymentStatus.PartiallyRefunded,
  PaymentStatus.Refunded,
  PaymentStatus.Failed,
  PaymentStatus.Disputed,
];
const FULFILLMENT_STATUS_OPTIONS: string[] = [
  "",
  FulfillmentStatus.Unfulfilled,
  FulfillmentStatus.PartiallyFulfilled,
  FulfillmentStatus.Fulfilled,
  FulfillmentStatus.Shipped,
  FulfillmentStatus.Delivered,
  FulfillmentStatus.Returned,
];

function Pill({
  text,
  tone = "slate",
}: {
  text?: string;
  tone?: "slate" | "green" | "blue" | "amber" | "red";
}) {
  const map: Record<string, { bg: string; fg: string }> = {
    slate: { bg: "#f3f4f6", fg: "#374151" },
    green: { bg: "#ecfdf5", fg: "#065f46" },
    blue: { bg: "#eef2ff", fg: "#3730a3" },
    amber: { bg: "#fff7ed", fg: "#9a3412" },
    red: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const c = map[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.7,
        whiteSpace: "nowrap",
      }}
    >
      {text || "-"}
    </span>
  );
}
const eq = (a: unknown, b: unknown) => String(a) === String(b);
const isFullyRefundedOrder = (order: any) =>
  eq(order?.paymentStatus, PaymentStatus.Refunded) ||
  Boolean(order?.refundSummary?.fullyRefunded);
const orderTone = (order: any): "slate" | "green" | "blue" | "amber" | "red" => {
  if (isFullyRefundedOrder(order) || eq(order?.status, OrderStatus.Refunded)) {
    return "red";
  }
  if (eq(order?.status, OrderStatus.PartiallyRefunded)) return "amber";
  if (eq(order?.status, OrderStatus.Pending)) return "amber";
  if (eq(order?.status, OrderStatus.Cancelled)) return "red";
  return "blue";
};
const paymentTone = (
  status?: PaymentStatus | string
): "slate" | "green" | "blue" | "amber" | "red" => {
  if (eq(status, PaymentStatus.Paid)) return "green";
  if (eq(status, PaymentStatus.Unpaid) || eq(status, PaymentStatus.Refunded)) {
    return "red";
  }
  if (eq(status, PaymentStatus.PartiallyRefunded)) return "amber";
  return "blue";
};

export default function OrdersListPage() {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const [items, setItems] = useState<OrderListItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sort, setSort] = useState("-createdAt");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [fulfillmentStatus, setFulfillmentStatus] = useState<string>("");

  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const query: ListQuery = useMemo(
    () => ({ page, limit, sort, q, status, paymentStatus, fulfillmentStatus }),
    [page, limit, sort, q, status, paymentStatus, fulfillmentStatus]
  );

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const data: OrderListResponse = await OrdersAPI.list(query as any);
        if (!on) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      } catch (e: any) {
        if (!on) return;
        setErr(
          e?.response?.data?.message || e?.message || "Failed to load orders"
        );
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [query]);

  const apply = () => setPage(1);

  const toggleSort = (field: string) => {
    setSort((cur) => {
      const clean = cur.replace(/^-/, "");
      if (clean === field && !cur.startsWith("-")) return `-${field}`; // asc -> desc
      return field; // set asc
    });
    setPage(1);
  };

  return (
    <div style={{ padding: isCompact ? 12 : 16, maxWidth: 1480, margin: "0 auto" }}>
      <h1 style={{ margin: 0 }}>Orders</h1>

      {/* Filters */}
      <div
        style={{
          marginTop: 12,
          display: "grid",
          gridTemplateColumns: isCompact
            ? "minmax(0, 1fr)"
            : "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: 8,
          alignItems: "center",
        }}
      >
        <input
          placeholder="Search any order field (text or number)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
        >
          {ORDER_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || "All Statuses"}
            </option>
          ))}
        </select>
        <select
          value={paymentStatus}
          onChange={(e) => {
            setPaymentStatus(e.target.value);
            setPage(1);
          }}
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
        >
          {PAYMENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || "All Payments"}
            </option>
          ))}
        </select>
        <select
          value={fulfillmentStatus}
          onChange={(e) => {
            setFulfillmentStatus(e.target.value);
            setPage(1);
          }}
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
        >
          {FULFILLMENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || "All Fulfillment"}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }}
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8 }}
        >
          <option value="-createdAt">Newest first</option>
          <option value="createdAt">Oldest first</option>
          <option value="-totals.grandTotal">Total: High → Low</option>
          <option value="totals.grandTotal">Total: Low → High</option>
        </select>
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          onClick={apply}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#111827",
            color: "white",
            fontWeight: 600,
          }}
        >
          Apply
        </button>
      </div>

      {/* Table */}
      {isCompact ? (
        <MobileOrdersList items={items} loading={loading} />
      ) : (
        <div
          style={{
            marginTop: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: 980,
              }}
            >
              <thead style={{ background: "#f9fafb" }}>
                <tr>
                  <th
                    style={{ padding: 8, textAlign: "left", cursor: "pointer" }}
                    onClick={() => toggleSort("createdAt")}
                  >
                    Order #
                  </th>
                  <th style={{ padding: 8, textAlign: "left" }}>Email</th>
                  <th
                    style={{ padding: 8, textAlign: "left", cursor: "pointer" }}
                    onClick={() => toggleSort("totals.grandTotal")}
                  >
                    Total
                  </th>
                  <th style={{ padding: 8, textAlign: "left" }}>Items</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Payment</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Fulfillment</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Tracking</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Inv.</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const itemsCount = Array.isArray((o as any).items)
                    ? (o as any).items.reduce(
                        (s: number, it: any) => s + (it.quantity || 0),
                        0
                      )
                    : undefined;
                  const trackingCount =
                    (o as any).shipping?.trackingNumbers?.length || 0;
                  const inv = (o as any).inventoryAdjusted ? "✓" : "✗";
                  const invTone = (o as any).inventoryAdjusted ? "green" : "amber";
                  const refundedTotal = Number((o as any).refundSummary?.totalRefunded || 0);
                  const netTotal = Number(
                    (o as any).refundSummary?.netTotal ??
                      Math.max(
                        0,
                        Number(o.totals?.grandTotal || 0) - refundedTotal
                      )
                  );
                  const fullyRefunded = isFullyRefundedOrder(o);

                  return (
                    <tr
                      key={o._id || o.orderNumber}
                      style={{
                        background: fullyRefunded ? "#fef2f2" : undefined,
                        opacity: fullyRefunded ? 0.78 : 1,
                      }}
                    >
                      <td style={{ padding: 8 }}>
                        <Link
                          to={`/orders/${encodeURIComponent(o.orderNumber)}`}
                          style={{
                            color: fullyRefunded ? "#991b1b" : "#2563eb",
                            textDecoration: "none",
                            fontWeight: fullyRefunded ? 800 : undefined,
                          }}
                        >
                          {o.orderNumber}
                        </Link>
                        {fullyRefunded ? (
                          <div style={{ fontSize: 11, color: "#991b1b" }}>
                            Inactive
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: 8 }}>{o.lookupEmail}</td>
                      <td style={{ padding: 8 }}>
                        {o.totals?.grandTotal?.toFixed?.(2)} {o.currency}
                        {refundedTotal > 0 ? (
                          <div style={{ fontSize: 11, color: "#dc2626" }}>
                            {netTotal.toFixed(2)} {o.currency} net
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: 8 }}>{itemsCount ?? "-"}</td>
                      <td style={{ padding: 8 }}>
                        <Pill
                          text={o.status}
                          tone={orderTone(o)}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <Pill
                          text={o.paymentStatus}
                          tone={paymentTone(o.paymentStatus)}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <Pill
                          text={o.fulfillmentStatus}
                          tone={
                            eq(o.fulfillmentStatus, FulfillmentStatus.Delivered)
                              ? "green"
                              : eq(o.fulfillmentStatus, FulfillmentStatus.Returned)
                              ? "red"
                              : "blue"
                          }
                        />
                      </td>
                      <td style={{ padding: 8 }}>{trackingCount || "-"}</td>
                      <td style={{ padding: 8 }}>
                        <Pill text={inv} tone={invTone as any} />
                      </td>
                      <td style={{ padding: 8 }}>
                        {o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}
                      </td>
                    </tr>
                  );
                })}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: 12, color: "#6b7280" }}>
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 12,
          justifyContent: "space-between",
        }}
      >
        <div style={{ color: "#6b7280", fontSize: 12 }}>
          {total} total • page {page} of {pages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: page <= 1 ? "#f9fafb" : "white",
            }}
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: page >= pages ? "#f9fafb" : "white",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {loading && <div style={{ marginTop: 8 }}>Loading…</div>}
      {err && <div style={{ marginTop: 8, color: "#b91c1c" }}>{err}</div>}
    </div>
  );
}

function MobileOrdersList({
  items,
  loading,
}: {
  items: OrderListItem[];
  loading: boolean;
}) {
  if (!items.length) {
    return (
      <div
        style={{
          marginTop: 12,
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          background: "#fff",
          padding: "14px 16px",
          color: "#6b7280",
        }}
      >
        {loading ? "Loading orders..." : "No orders found"}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
      {items.map((order) => {
        const itemsCount = Array.isArray((order as any).items)
          ? (order as any).items.reduce(
              (sum: number, item: any) => sum + (item.quantity || 0),
              0
            )
          : undefined;
        const trackingCount = (order as any).shipping?.trackingNumbers?.length || 0;
        const inventoryAdjusted = Boolean((order as any).inventoryAdjusted);
        const refundedTotal = Number((order as any).refundSummary?.totalRefunded || 0);
        const netTotal = Number(
          (order as any).refundSummary?.netTotal ??
            Math.max(0, Number(order.totals?.grandTotal || 0) - refundedTotal)
        );
        const fullyRefunded = isFullyRefundedOrder(order);

        return (
          <Link
            key={order._id || order.orderNumber}
            to={`/orders/${encodeURIComponent(order.orderNumber)}`}
            style={{
              border: fullyRefunded ? "1px solid #fecaca" : "1px solid #e5e7eb",
              borderRadius: 16,
              background: fullyRefunded ? "#fef2f2" : "#fff",
              padding: 14,
              display: "grid",
              gap: 10,
              textDecoration: "none",
              opacity: fullyRefunded ? 0.82 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <div style={{ color: "#0f172a", fontSize: 16, fontWeight: 800 }}>
                  {order.orderNumber}
                </div>
                {fullyRefunded ? (
                  <div style={{ color: "#991b1b", fontSize: 12, fontWeight: 800 }}>
                    Inactive · Fully refunded
                  </div>
                ) : null}
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  {order.lookupEmail || "No email"}
                </div>
              </div>
              <div style={{ color: "#0f172a", fontWeight: 800 }}>
                {order.totals?.grandTotal?.toFixed?.(2)} {order.currency}
                {refundedTotal > 0 ? (
                  <div style={{ fontSize: 11, color: "#dc2626", textAlign: "right" }}>
                    {netTotal.toFixed(2)} {order.currency} net
                  </div>
                ) : null}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Pill
                text={order.status}
                tone={orderTone(order)}
              />
              <Pill
                text={order.paymentStatus}
                tone={paymentTone(order.paymentStatus)}
              />
              <Pill
                text={order.fulfillmentStatus}
                tone={
                  eq(order.fulfillmentStatus, FulfillmentStatus.Delivered)
                    ? "green"
                    : eq(order.fulfillmentStatus, FulfillmentStatus.Returned)
                    ? "red"
                    : "blue"
                }
              />
            </div>

            <div
              style={{
                display: "grid",
                gap: 6,
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                color: "#334155",
                fontSize: 13,
              }}
            >
              <div>
                <strong>Items:</strong> {itemsCount ?? "-"}
              </div>
              <div>
                <strong>Tracking:</strong> {trackingCount || "-"}
              </div>
              <div>
                <strong>Inventory:</strong> {inventoryAdjusted ? "Adjusted" : "Pending"}
              </div>
              <div>
                <strong>Created:</strong>{" "}
                {order.createdAt ? new Date(order.createdAt).toLocaleString() : "-"}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
